import { useEffect } from "react";
import type { TLRecord, TLShape, TLShapeId } from "@tldraw/tlschema";
import type { Editor } from "./canvas";
import {
  clearEntrances,
  queueEntrance,
  queueSceneChange,
  type EntranceKind,
} from "./canvas/engine/shape-entrance";
import { changedElements, readSceneSpec } from "../shared/scene-spec";

/**
 * Agent edits commit as one validated batch and reach every open board as a
 * remote store change. This choreographs that batch: sheets settle first, then
 * contents appear in the agent's operation order, strokes drawing on. The
 * person who asked also gets a camera glide when the work landed offscreen.
 * Nothing here changes records; it only animates what already arrived.
 */

/** Agent-created shapes carry their conversation prefix: `shape:d<8 hex>_…`. */
const AGENT_SHAPE = /^shape:d([0-9a-f]{8})_/;

export function agentPrefix(id: string) {
  return AGENT_SHAPE.exec(id)?.[1];
}

export function entranceKind(shape: Pick<TLShape, "type">): EntranceKind | null {
  switch (shape.type) {
    case "group":
      return null;
    case "scene":
      return "scene";
    case "sheet":
      return "sheet";
    case "text":
    case "note":
      return "text";
    case "image":
    case "video":
      return "image";
    case "line":
    case "draw":
    case "arrow":
    case "drafting":
    case "mark":
    case "highlight":
      return "stroke";
    default:
      return "shape";
  }
}

export type PlannedEntrance = { id: string; kind: EntranceKind; delay: number };

/** Stagger budget for one batch, whatever its size (ms). */
const SPREAD_MS = 1600;

/**
 * Plan entrances one created record at a time, in arrival (operation) order:
 * agent sheets settle first, contents follow on a stagger that tightens as the
 * batch grows, so even a 25-operation batch lands within about two seconds.
 * The store reports creations synchronously, before React mounts the shapes,
 * so each entrance must be decided as its record arrives.
 */
export function entrancePlanner() {
  let sheets = 0,
    rest = 0;
  return (record: TLRecord): PlannedEntrance | null => {
    if (record.typeName !== "shape" || !agentPrefix(record.id)) return null;
    const kind = entranceKind(record as TLShape);
    if (!kind) return null;
    if (kind === "sheet") return { id: record.id, kind, delay: sheets++ * 140 };
    const start = sheets ? 220 + (sheets - 1) * 140 : 0;
    const delay = SPREAD_MS * (1 - Math.exp(-rest++ / 20));
    return { id: record.id, kind, delay: Math.round(start + delay) };
  };
}

/** Plan a whole batch at once (same rules as the incremental planner). */
export function planEntrances(added: TLRecord[]): PlannedEntrance[] {
  const sheetsFirst = [
    ...added.filter((r) => r.typeName === "shape" && (r as TLShape).type === "sheet"),
    ...added.filter((r) => !(r.typeName === "shape" && (r as TLShape).type === "sheet")),
  ];
  const next = entrancePlanner();
  return sheetsFirst.flatMap((r) => next(r) ?? []);
}

/** Conversations whose output this tab follows with the camera, with expiry. */
const followed = new Map<string, number>();

/** Follow a conversation's new work with the camera until `ms` from now. */
export function followAgent(draftId: string, ms = 10 * 60_000) {
  followed.set(draftId.slice(0, 8), Date.now() + ms);
}

function follows(prefix: string) {
  const until = followed.get(prefix);
  if (until === undefined) return false;
  if (until < Date.now()) {
    followed.delete(prefix);
    return false;
  }
  return true;
}

const reducedMotion = () =>
  typeof matchMedia === "function" &&
  matchMedia("(prefers-reduced-motion: reduce)").matches;

/** Width of an open agent panel docked over the right of the board, in screen pixels. */
function panelCover(screen: { x: number; w: number }) {
  const panel =
    typeof document === "undefined" ? null : document.querySelector(".agent-panel")?.getBoundingClientRect();
  return panel && panel.width && panel.left > screen.x + screen.w / 2
    ? screen.x + screen.w - panel.left + 16
    : 0;
}

/** Glide to followed new work unless most of it is already in view (beside the agent panel). */
function glideTo(editor: Editor, ids: TLShapeId[]) {
  const page = editor.getCurrentPageId();
  const boxes = ids
    .filter((id) => editor.getShape(id) && editor.getAncestorPageId(id) === page)
    .map((id) => editor.getShapePageBounds(id))
    .filter((b): b is NonNullable<typeof b> => !!b);
  if (!boxes.length) return;
  const x = Math.min(...boxes.map((b) => b.x)),
    y = Math.min(...boxes.map((b) => b.y)),
    w = Math.max(...boxes.map((b) => b.x + b.w)) - x,
    h = Math.max(...boxes.map((b) => b.y + b.h)) - y;
  const screen = editor.getViewportScreenBounds();
  const reserved = panelCover(screen);
  const a = editor.pageToScreen({ x, y }),
    b = editor.pageToScreen({ x: x + w, y: y + h });
  const seenW = Math.max(0, Math.min(b.x, screen.x + screen.w - reserved) - Math.max(a.x, screen.x)),
    seenH = Math.max(0, Math.min(b.y, screen.y + screen.h) - Math.max(a.y, screen.y));
  const area = Math.max(1, (b.x - a.x) * (b.y - a.y));
  if ((seenW * seenH) / area > 0.6) return;
  const fit = Math.min(
    (screen.w - reserved - 240) / Math.max(w, 1),
    (screen.h - 200) / Math.max(h, 1),
  );
  const z = Math.max(0.05, Math.min(editor.getZoomLevel(), fit));
  // Widen the frame by the panel so the work lands beside it, not under it.
  editor.zoomToBounds({ x, y, w: w + reserved / z, h }, { targetZoom: z, animation: { duration: 900 } });
}

/** Choreograph agent-made shapes as they arrive from the server. */
export function useAgentEntrances(editor: Editor) {
  useEffect(() => {
    if (reducedMotion()) return;
    let next: ReturnType<typeof entrancePlanner> | null = null;
    let mine: TLShapeId[] = [];
    const stop = editor.sideEffects.registerAfterCreateHandler(
      "shape",
      (shape, source) => {
        if (source !== "remote") return;
        // One planner per synchronous batch; a microtask closes the batch.
        if (!next) {
          next = entrancePlanner();
          queueMicrotask(() => {
            next = null;
            const follow = mine;
            mine = [];
            if (follow.length) requestAnimationFrame(() => glideTo(editor, follow));
          });
        }
        const entry = next(shape);
        if (!entry) return;
        queueEntrance(entry.id, entry.kind, entry.delay);
        if (follows(agentPrefix(entry.id)!)) mine.push(entry.id as TLShapeId);
      },
    );
    // An edited 3D model rebuilds only the parts that changed.
    const stopChanges = editor.sideEffects.registerAfterChangeHandler(
      "shape",
      (prev, shape, source) => {
        if (source !== "remote" || shape.type !== "scene") return;
        const before = (prev.props as { spec: string }).spec,
          after = (shape.props as { spec: string }).spec;
        if (before === after) return;
        queueSceneChange(shape.id, changedElements(readSceneSpec(before), readSceneSpec(after)));
      },
    );
    return () => {
      stop();
      stopChanges();
      clearEntrances();
    };
  }, [editor]);
}
